import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, RouterStateSnapshot } from '@angular/router';
import { Router } from '@angular/router';

import { LanguageService } from '@rpgc-ai/services/translate/language.service';
import type { SupportedLanguageCode } from '@rpgc-ai/services/translate/languages';
import { DEFAULT_LANGUAGE } from '@rpgc-ai/services/translate/languages';

import { TranslocoService } from '@jsverse/transloco';

@Injectable({ providedIn: 'root' })
export class LanguageGuard implements CanActivate {
  // Injections
  readonly #router = inject(Router);

  readonly #languageService = inject(LanguageService);
  readonly #translocoService = inject(TranslocoService);

  canActivate(_route: ActivatedRouteSnapshot, { url }: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const ignoredLanguageRedirect = this.#languageService.getIgnoredLanguageHistoryRedirect(url);
    if (ignoredLanguageRedirect) {
      return ignoredLanguageRedirect;
    }

    // `?` and `#` terminate the locale segment as well: RouterStateSnapshot.url carries the
    // query string and fragment, and referral links are minted as `/{lang}?referralCode=...`.
    const langFromPath = url.match(/^\/([a-z]{2})(?:\/|\(|\?|#|$)/)?.[1] as SupportedLanguageCode;

    // URL language takes priority — user explicitly navigated to this language
    if (this.#languageService.isLangValid(langFromPath)) {
      if (this.#translocoService.getActiveLang() !== langFromPath) {
        this.#translocoService.setActiveLang(langFromPath);
      }
      this.#languageService.setNewLanguageToCookies(langFromPath);
      return true;
    }

    // No valid lang in URL — detect from cookie/browser and redirect
    const detectedLang = this.#languageService.initLanguage() || DEFAULT_LANGUAGE;

    if (this.#translocoService.getActiveLang() !== detectedLang) {
      this.#translocoService.setActiveLang(detectedLang);
    }
    this.#languageService.setNewLanguageToCookies(detectedLang);

    return this.#router.parseUrl(`/${detectedLang}${url}`);
  }
}
